import React, { useMemo, useState } from 'react';
import {
  aggregateCallMetrics,
  normalizeCallOutcomeSlug,
  CALL_OUTCOME_LABELS,
  callMatchesUser,
} from '../../utils/callOutcomeAnalytics';
import { computeRange, actDate, toYMD, RANGE_OPTS } from '../../utils/activityDashboardData';

const OUTCOME_FILTERS = [
  { id: 'all', label: 'Alle' },
  { id: 'reached_appt', label: 'Termine' },
  { id: 'gatekeeper', label: 'Gatekeeper' },
  { id: 'decision_maker', label: 'Entscheider' },
  { id: 'reached_followup', label: 'Follow-up' },
  { id: 'callback', label: 'Rückruf' },
  { id: 'reached_no', label: 'Kein Interesse' },
  { id: 'not_voicemail', label: 'Voicemail' },
  { id: 'not_noanswer', label: 'Kein Anschluss' },
];

const OUTCOME_COLORS = {
  reached_appt: '#16a34a',
  gatekeeper: '#3186ba',
  decision_maker: '#0d9488',
  reached_followup: '#0f8a53',
  callback: '#d97706',
  reached_no: '#dc2626',
  not_voicemail: '#8f7cc9',
  not_busy: '#7a5fb8',
  not_noanswer: '#66796c',
  unknown: '#93a89a',
};

const REP_COLORS = ['#e87040', '#159c5f', '#22c55e', '#ab9bd6', '#fb7185', '#3186ba', '#f97316'];

function GlassCard({ children, style }) {
  return (
    <div style={{
      borderRadius: 16,
      padding: '18px 20px',
      background: 'rgba(255,255,255,0.06)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255,255,255,0.12)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), 0 8px 32px rgba(0,0,0,0.18)',
      ...style,
    }}>
      {children}
    </div>
  );
}

function MiniDonut({ segments, total }) {
  let cumul = 0;
  const r = 40, cx = 48, cy = 48, sw = 11;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={96} height={96} viewBox="0 0 96 96" style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={sw} />
      {segments.filter(s => s.pct > 0).map((seg, i) => {
        const start = cumul; cumul += seg.pct;
        const dashArr = `${(seg.pct / 100) * circ} ${circ}`;
        const offset = circ * (1 - start / 100);
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={seg.color}
            strokeWidth={sw} strokeDasharray={dashArr} strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ transition: 'stroke-dasharray .5s ease' }} />
        );
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="22" fontWeight="900" fill="var(--text)">{total}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="9" fontWeight="600" fill="rgba(255,255,255,0.4)">ANRUFE</text>
    </svg>
  );
}

function SparkBar({ data, maxVal }) {
  const max = maxVal || Math.max(1, ...data.map(d => d.count));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80, width: '100%' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
          <div style={{ width: '100%', position: 'relative', height: `${Math.max(d.count > 0 ? 2 : 0, (d.count / max) * 72)}px`, minHeight: d.count > 0 ? 2 : 0 }}>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, #159c5fbb, #0d7647)', borderRadius: '3px 3px 0 0' }} />
            {d.termine > 0 && (
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${(d.termine / d.count) * 100}%`, background: '#16a34a', borderRadius: d.termine === d.count ? '3px 3px 0 0' : 0 }} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function TrendChip({ val }) {
  if (val === null || val === undefined) return null;
  const up = val >= 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700, color: up ? '#22c55e' : '#f87171', background: up ? 'rgba(34,197,94,0.12)' : 'rgba(248,113,113,0.12)', borderRadius: 6, padding: '2px 6px' }}>
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        {up ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />}
      </svg>
      {Math.abs(val)}%
    </span>
  );
}

export default function RepDetailModal({ user, userIdx = 0, allCallsRaw, leads, authUsers, onClose, onOpenLead }) {
  const [rangeId, setRangeId] = useState('30d');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date');

  const color = REP_COLORS[userIdx % REP_COLORS.length];

  const { start, end, prevStart, prevEnd } = useMemo(() => computeRange(rangeId), [rangeId]);

  const todayStart = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const todayEnd = useMemo(() => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; }, []);

  const myCalls = useMemo(
    () => allCallsRaw.filter(c => c.type === 'call' && callMatchesUser(c, user.id, authUsers)),
    [allCallsRaw, user.id, authUsers]
  );

  const periodCalls = useMemo(
    () => myCalls.filter(c => { const d = actDate(c); return d && d >= start && d <= end; }),
    [myCalls, start, end]
  );

  const prevCalls = useMemo(
    () => myCalls.filter(c => { const d = actDate(c); return d && d >= prevStart && d <= prevEnd; }),
    [myCalls, prevStart, prevEnd]
  );

  const todayCalls = useMemo(
    () => myCalls.filter(c => { const d = actDate(c); return d && d >= todayStart && d <= todayEnd; }),
    [myCalls, todayStart, todayEnd]
  );

  const metrics = useMemo(() => aggregateCallMetrics(periodCalls), [periodCalls]);
  const prevMetrics = useMemo(() => aggregateCallMetrics(prevCalls), [prevCalls]);
  const todayMetrics = useMemo(() => aggregateCallMetrics(todayCalls), [todayCalls]);

  function trend(curr, prev) {
    if (prev === 0) return null;
    return Math.round(((curr - prev) / prev) * 100);
  }

  const geldgeberCalls = useMemo(() => periodCalls.filter(c => {
    const l = c.lead;
    if (!l) return false;
    const check = [l.type, l.kategorie, l.category, l.leadType, l.geldgeber, l.branche, l.tags]
      .filter(Boolean).join(' ').toLowerCase();
    return l.geldgeber === true || check.includes('geldgeber') || check.includes('investor');
  }), [periodCalls]);

  const filteredList = useMemo(() => {
    let list = [...periodCalls];
    if (outcomeFilter !== 'all') list = list.filter(c => normalizeCallOutcomeSlug(c) === outcomeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => {
        const l = c.lead;
        return [l?.name, l?.firma, l?.company, l?.ansprechpartner, CALL_OUTCOME_LABELS[normalizeCallOutcomeSlug(c)], c.note, c.notes]
          .filter(Boolean).join(' ').toLowerCase().includes(q);
      });
    }
    if (sortBy === 'date') list.sort((a, b) => new Date(b.ts || b.date || 0) - new Date(a.ts || a.date || 0));
    if (sortBy === 'outcome') list.sort((a, b) => normalizeCallOutcomeSlug(a).localeCompare(normalizeCallOutcomeSlug(b)));
    if (sortBy === 'firm') list.sort((a, b) => (a.lead?.firma || a.lead?.name || '').localeCompare(b.lead?.firma || b.lead?.name || ''));
    return list;
  }, [periodCalls, outcomeFilter, search, sortBy]);

  const donutSegments = useMemo(() => {
    const map = {};
    periodCalls.forEach(c => {
      const s = normalizeCallOutcomeSlug(c);
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map)
      .filter(([, v]) => v > 0)
      .map(([slug, count]) => ({
        slug, label: CALL_OUTCOME_LABELS[slug] || slug, count,
        pct: metrics.total > 0 ? (count / metrics.total) * 100 : 0,
        color: OUTCOME_COLORS[slug] || '#93a89a',
      }))
      .sort((a, b) => b.count - a.count);
  }, [periodCalls, metrics.total]);

  const callsPerDay = useMemo(() => {
    const count = rangeId === 'heute' ? 1 : rangeId === '7d' ? 7 : rangeId === '30d' ? 14 : 28;
    const now = new Date(); now.setHours(23, 59, 59, 999);
    const days = [];
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i); d.setHours(0, 0, 0, 0);
      days.push(toYMD(d));
    }
    const callMap = {}, termMap = {};
    periodCalls.forEach(c => {
      const d = actDate(c);
      if (!d) return;
      const k = toYMD(d);
      callMap[k] = (callMap[k] || 0) + 1;
      if (normalizeCallOutcomeSlug(c) === 'reached_appt') termMap[k] = (termMap[k] || 0) + 1;
    });
    return days.map(day => ({
      label: new Date(day + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
      count: callMap[day] || 0,
      termine: termMap[day] || 0,
    }));
  }, [periodCalls, rangeId]);

  const avgQuality = useMemo(() => {
    const rated = periodCalls.filter(c => c.callQuality > 0);
    return rated.length > 0 ? (rated.reduce((s, c) => s + (c.callQuality || 0), 0) / rated.length).toFixed(1) : null;
  }, [periodCalls]);

  const initials = (user.name || user.email || '?').split(' ').map(x => x[0] || '').join('').slice(0, 2).toUpperCase();
  const rangeLabel = RANGE_OPTS.find(r => r.id === rangeId)?.label || '';

  const kpis = [
    {
      label: 'Heute', value: todayMetrics.total, sub: `${todayCalls.filter(c => normalizeCallOutcomeSlug(c) === 'reached_appt').length} Termine heute`,
      color: '#e87040', trend: null,
    },
    {
      label: 'Anrufe gesamt', value: metrics.total, sub: rangeLabel,
      color: '#159c5f', trend: trend(metrics.total, prevMetrics.total),
    },
    {
      label: 'Termine gesetzt', value: metrics.termin, sub: `${metrics.pctTermin}% Abschlussrate`,
      color: '#16a34a', trend: trend(metrics.termin, prevMetrics.termin),
    },
    {
      label: 'Gatekeeper', value: metrics.gatekeeper, sub: `${metrics.pctGate}% aller Anrufe`,
      color: '#3186ba', trend: null,
    },
    {
      label: 'Entscheider', value: metrics.decider, sub: `${metrics.pctDecider}% aller Anrufe`,
      color: '#0d9488', trend: trend(metrics.decider, prevMetrics.decider),
    },
    {
      label: 'Geldgeber', value: geldgeberCalls.length, sub: 'Leads erreicht',
      color: '#f59e0b', trend: null,
    },
    {
      label: 'Follow-up', value: metrics.followup, sub: `${metrics.pctFollowup}%`,
      color: '#8f7cc9', trend: null,
    },
    {
      label: 'Rückrufe', value: metrics.callback, sub: `${metrics.pctCallback}%`,
      color: '#d97706', trend: null,
    },
  ];

  const SortBtn = ({ id, label }) => (
    <button onClick={() => setSortBy(id)} style={{
      padding: '4px 10px', borderRadius: 6, border: `1px solid ${sortBy === id ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)'}`,
      background: sortBy === id ? 'rgba(255,255,255,0.1)' : 'transparent',
      fontSize: 11, color: sortBy === id ? 'var(--text)' : 'var(--text3)', cursor: 'pointer', fontWeight: sortBy === id ? 700 : 500,
    }}>{label}</button>
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(8,12,24,0.7)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ flex: 1, overflowY: 'auto', padding: '28px clamp(16px, 4vw, 40px)' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto' }}>

          {/* ── Header ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
            <button onClick={onClose} aria-label="Zurück" style={{
              width: 40, height: 40, borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(10px)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text)" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <div style={{
              width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${color}, ${color}88)`,
              border: `2.5px solid ${color}55`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 19, fontWeight: 900, color: '#fff',
              boxShadow: `0 0 20px ${color}44`,
            }}>{initials}</div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--text)', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {user.name || user.email}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>{user.role || 'Mitarbeiter'}</span>
                {user.email && user.name && <><span style={{ color: 'rgba(255,255,255,0.2)' }}>·</span><span style={{ fontSize: 12, color: 'var(--text3)' }}>{user.email}</span></>}
                {avgQuality && (
                  <><span style={{ color: 'rgba(255,255,255,0.2)' }}>·</span>
                  <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 700 }}>{'★'.repeat(Math.round(avgQuality))}{'☆'.repeat(5 - Math.round(avgQuality))} {avgQuality}/5</span></>
                )}
              </div>
            </div>

            {/* Range selector */}
            <div style={{
              display: 'flex', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 12, overflow: 'hidden', backdropFilter: 'blur(10px)', flexShrink: 0,
            }}>
              {RANGE_OPTS.map(r => (
                <button key={r.id} onClick={() => setRangeId(r.id)} style={{
                  height: 38, padding: '0 16px', fontSize: 12, fontWeight: rangeId === r.id ? 700 : 500,
                  cursor: 'pointer', border: 'none', background: rangeId === r.id ? 'rgba(255,255,255,0.14)' : 'transparent',
                  color: rangeId === r.id ? 'var(--text)' : 'var(--text3)', transition: 'all .12s',
                }}>{r.label}</button>
              ))}
            </div>
          </div>

          {/* ── KPI Grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
            {kpis.map(k => (
              <div key={k.label} style={{
                borderRadius: 14, padding: '14px 16px',
                background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px) saturate(180%)', WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                border: '1px solid rgba(255,255,255,0.1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12), 0 4px 20px rgba(0,0,0,0.12)',
              }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>{k.label}</div>
                <div style={{ fontSize: 34, fontWeight: 900, color: k.color, lineHeight: 1, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 5, lineHeight: 1.3 }}>{k.sub}</div>
                {k.trend !== null && k.trend !== undefined && (
                  <div style={{ marginTop: 6 }}><TrendChip val={k.trend} /></div>
                )}
              </div>
            ))}
          </div>

          {/* ── Charts row ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: 14, marginBottom: 20 }}>
            {/* Bar chart */}
            <GlassCard>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>Anrufe pro Tag</div>
              {callsPerDay.some(d => d.count > 0) ? (
                <>
                  <SparkBar data={callsPerDay} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {callsPerDay.filter((_, i) => callsPerDay.length <= 10 || i % Math.ceil(callsPerDay.length / 10) === 0).map((d, i) => (
                      <span key={i} style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', whiteSpace: 'nowrap' }}>{d.label}</span>
                    ))}
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#159c5f', display: 'inline-block' }} />Anrufe
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: '#16a34a', display: 'inline-block', marginLeft: 6 }} />Termine
                    </span>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '20px 0' }}>Keine Daten im Zeitraum</div>
              )}
            </GlassCard>

            {/* Donut */}
            <GlassCard style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>Ergebnisse</div>
              {donutSegments.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <MiniDonut segments={donutSegments} total={metrics.total} />
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {donutSegments.slice(0, 6).map(s => (
                      <div key={s.slug} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                        <div style={{ flex: 1, fontSize: 11, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', flexShrink: 0, fontFamily: 'var(--mono)' }}>{s.count}</div>
                        <div style={{ fontSize: 9, color: 'var(--text3)', width: 30, textAlign: 'right', flexShrink: 0 }}>{Math.round(s.pct)}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, paddingTop: 20 }}>Keine Daten</div>
              )}
            </GlassCard>
          </div>

          {/* ── Anruf-Protokoll ── */}
          <GlassCard style={{ padding: 0, overflow: 'hidden' }}>
            {/* Filters header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginRight: 4, flexShrink: 0 }}>Anruf-Protokoll</div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', flex: 1 }}>
                {OUTCOME_FILTERS.map(f => {
                  const cnt = f.id === 'all' ? periodCalls.length : periodCalls.filter(c => normalizeCallOutcomeSlug(c) === f.id).length;
                  const active = outcomeFilter === f.id;
                  return (
                    <button key={f.id} onClick={() => setOutcomeFilter(f.id)} style={{
                      height: 26, padding: '0 10px', borderRadius: 20,
                      border: `1.5px solid ${active ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.07)'}`,
                      background: active ? 'rgba(255,255,255,0.1)' : 'transparent',
                      fontSize: 11, fontWeight: active ? 700 : 500, color: active ? 'var(--text)' : 'var(--text3)',
                      cursor: 'pointer', backdropFilter: 'blur(8px)', transition: 'all .12s',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      {f.label}
                      <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.65 }}>({cnt})</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <SortBtn id="date" label="Neueste" />
                  <SortBtn id="outcome" label="Ergebnis" />
                  <SortBtn id="firm" label="Firma" />
                </div>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen..."
                  style={{
                    height: 30, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.06)', color: 'var(--text)', fontSize: 12, padding: '0 10px',
                    outline: 'none', width: 150, backdropFilter: 'blur(8px)', fontFamily: 'inherit',
                  }} />
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto', maxHeight: 440, overflowY: 'auto' }}>
              {filteredList.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: '40px 20px' }}>
                  Keine Anrufe gefunden.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{ background: 'rgba(8,12,24,0.8)', backdropFilter: 'blur(10px)' }}>
                      {['Datum', 'Zeit', 'Firma / Kontakt', 'Ansprechpartner', 'Ergebnis', 'Notiz', ''].map((h, i) => (
                        <th key={i} style={{
                          padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                          color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.06em',
                          borderBottom: '1px solid rgba(255,255,255,0.07)', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map((c, i) => {
                      const d = actDate(c);
                      const slug = normalizeCallOutcomeSlug(c);
                      const color2 = OUTCOME_COLORS[slug] || '#93a89a';
                      const lead = c.lead;
                      const firma = lead?.firma || lead?.company || lead?.name || '–';
                      const kontakt = lead?.ansprechpartner || lead?.contactName || (lead?.name !== firma ? lead?.name : null) || '–';
                      return (
                        <tr key={i}
                          style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: lead ? 'pointer' : 'default', transition: 'background .1s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          onClick={() => { if (lead) { onClose(); onOpenLead?.(lead); } }}>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                            {d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '–'}
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', fontFamily: 'var(--mono)' }}>
                            {d ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '–'}
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text)', fontWeight: 600, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {firma}
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text2)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {kontakt}
                          </td>
                          <td style={{ padding: '10px 16px', whiteSpace: 'nowrap' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '3px 9px', borderRadius: 20,
                              background: color2 + '18', border: `1px solid ${color2}30`,
                              fontSize: 11, fontWeight: 600, color: color2,
                            }}>
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: color2, flexShrink: 0 }} />
                              {CALL_OUTCOME_LABELS[slug] || c.outcome || '–'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 11, color: 'var(--text3)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.note || c.notes || <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                            {lead && (
                              <button
                                onClick={e => { e.stopPropagation(); onClose(); onOpenLead?.(lead); }}
                                style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                                Öffnen →
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {filteredList.length > 0 && (
              <div style={{ padding: '10px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 12 }}>
                <span>{filteredList.length} Einträge</span>
                {outcomeFilter !== 'all' && <span>· Gefiltert: {OUTCOME_FILTERS.find(f => f.id === outcomeFilter)?.label}</span>}
                {search && <span>· Suche: „{search}"</span>}
              </div>
            )}
          </GlassCard>

          <div style={{ height: 40 }} />
        </div>
      </div>
    </div>
  );
}
